import { Search, Filter, Star, Heart, MapPin, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

// Mock data voor demo - later vervangen door echte API
const mockServices = [
  {
    id: 1,
    title: "Marketing Strategy Consulting",
    provider: "Sarah van Dijk",
    category: "Business & Consulting",
    price: "€125",
    priceType: "per uur",
    rating: 4.9,
    reviews: 34,
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Amsterdam",
    description: "Boost je bedrijf met professionele marketing strategieën en groeiplannen op maat.",
    tags: ["Marketing", "Strategie", "Online"]
  },
  {
    id: 2,
    title: "Professional Photography",
    provider: "Luna Fotografie",
    category: "Creative Services",
    price: "€200",
    priceType: "per shoot",
    rating: 5.0,
    reviews: 67,
    image: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Utrecht",
    description: "Professionele portret- en bedrijfsfotografie die jouw persoonlijkheid en merk laat stralen.",
    tags: ["Fotografie", "Portretten", "Business"]
  },
  {
    id: 3,
    title: "Website Development",
    provider: "Maya Tech Solutions",
    category: "Tech & Digital",
    price: "€75",
    priceType: "per uur",
    rating: 4.8,
    reviews: 42,
    image: "https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Rotterdam",
    description: "Moderne, responsive websites die converteren. Van concept tot live website.",
    tags: ["Webdesign", "Development", "E-commerce"]
  },
  {
    id: 4,
    title: "Content Writing & Copywriting",
    provider: "Crystal Schrijft",
    category: "Creative Services",
    price: "€60",
    priceType: "per uur",
    rating: 4.7,
    reviews: 89,
    image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Online",
    description: "Krachtige teksten die verkopen. Van websiteteksten tot social media content.",
    tags: ["Content", "SEO", "Social Media"]
  },
  {
    id: 5,
    title: "Personal Branding Coach",
    provider: "Emma Brand Expert",
    category: "Business & Consulting",
    price: "€95",
    priceType: "per sessie",
    rating: 4.9,
    reviews: 28,
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Den Haag",
    description: "Ontdek en versterk je unieke personal brand. Van LinkedIn tot netwerken.",
    tags: ["Branding", "LinkedIn", "Netwerken"]
  },
  {
    id: 6,
    title: "Virtual Assistant Services",
    provider: "Iris Administratie",
    category: "Professional Services",
    price: "€35",
    priceType: "per uur",
    rating: 4.8,
    reviews: 156,
    image: "https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Eindhoven",
    description: "Professionele administratieve ondersteuning. Email, planning, data-entry en meer.",
    tags: ["Administratie", "Planning", "Email"]
  },
  {
    id: 7,
    title: "Social Media Management",
    provider: "Lotte Social",
    category: "Tech & Digital",
    price: "€450",
    priceType: "per maand",
    rating: 4.9,
    reviews: 23,
    image: "https://images.pexels.com/photos/267389/pexels-photo-267389.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Online",
    description: "Volledige social media strategie en uitvoering. Instagram, LinkedIn, Facebook.",
    tags: ["Social Media", "Content", "Strategie"]
  },
  {
    id: 8,
    title: "Graphic Design Services",
    provider: "Nina Design Studio",
    category: "Creative Services",
    price: "€65",
    priceType: "per uur",
    rating: 4.8,
    reviews: 91,
    image: "https://images.pexels.com/photos/196645/pexels-photo-196645.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Groningen",
    description: "Logo's, branding, flyers en digitale designs die opvallen en converteren.",
    tags: ["Design", "Logo", "Branding"]
  },
  {
    id: 9,
    title: "Business Coaching",
    provider: "Anke Business Coach",
    category: "Business & Consulting",
    price: "€110",
    priceType: "per sessie",
    rating: 5.0,
    reviews: 45,
    image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Haarlem",
    description: "Groei je bedrijf met praktische coaching. Van startup tot scale-up.",
    tags: ["Coaching", "Business", "Groei"]
  }
];

const categories = [
  "Alle categorieën",
  "Business & Consulting", 
  "Creative Services",
  "Tech & Digital",
  "Professional Services",
  "Lifestyle & Wellness",
  "Education & Training"
];

const ServicesList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Alle categorieën");
  const [favorites, setFavorites] = useState<number[]>([]);

  const toggleFavorite = (serviceId: number) => {
    setFavorites(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const filteredServices = mockServices.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "Alle categorieën" || 
                           service.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm sticky top-0 z-10" style={{ borderBottom: '1px solid #efe7dd' }}>
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/50"
              style={{ color: '#837069' }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold" style={{ color: '#837069' }}>
                Diensten Ontdekken
              </h1>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Vind de perfecte dienst van een woman on a mission
              </p>
            </div>
          </div>

          {/* Services Header */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-2xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>Diensten</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Ontdek expertise van ambitieuze vrouwen in België
              </p>
            </div>
          </div>
          {/* Search & Filter */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: '#b6a092' }} />
              <Input 
                placeholder="Zoek diensten, coaches, of specialiteiten..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                style={{ borderColor: '#efe7dd' }}
              />
            </div>
            <Button variant="outline" className="hover:bg-white/50" style={{ borderColor: '#efe7dd', color: '#837069' }}>
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(category => (
              <Badge
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className={`cursor-pointer whitespace-nowrap transition-all ${
                  selectedCategory === category 
                    ? "text-white hover:opacity-90" 
                    : "hover:bg-white/50"
                }`}
                style={selectedCategory === category 
                  ? { backgroundColor: '#a0897c', borderColor: '#a0897c' }
                  : { borderColor: '#efe7dd', color: '#837069' }
                }
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map(service => (
            <div 
              key={service.id}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              style={{ border: '1px solid #efe7dd' }}
              onClick={() => navigate(`/service/${service.id}`)}
            >
              {/* Service Image */}
              <div className="relative h-48" style={{ backgroundColor: '#efe7dd' }}>
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(service.id);
                  }}
                  className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                >
                  <Heart 
                    className={`h-4 w-4 ${
                      favorites.includes(service.id) 
                        ? "fill-red-400 text-red-400" 
                        : "text-gray-400"
                    }`} 
                  />
                </button>
              </div>

              {/* Service Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-lg" style={{ color: '#837069' }}>{service.title}</h3>
                  <div className="text-right">
                    <div className="font-semibold" style={{ color: '#a0897c' }}>{service.price}</div>
                    <div className="text-xs" style={{ color: '#b6a092' }}>{service.priceType}</div>
                  </div>
                </div>

                <p className="text-sm mb-3 line-clamp-2" style={{ color: '#b6a092' }}>
                  {service.description}
                </p>

                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium" style={{ color: '#837069' }}>
                    {service.provider}
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-medium">{service.rating}</span>
                    <span className="text-xs" style={{ color: '#b6a092' }}>({service.reviews})</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs" style={{ color: '#b6a092' }}>
                    <MapPin className="h-3 w-3" />
                    {service.location}
                  </div>
                  <div className="flex gap-1">
                    {service.tags.slice(0, 2).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs" style={{ backgroundColor: '#efe7dd', color: '#837069' }}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredServices.length === 0 && (
          <div className="text-center py-12">
            <Search className="h-16 w-16 mx-auto mb-4" style={{ color: '#d9cbbf' }} />
            <h2 className="text-xl font-medium mb-2" style={{ color: '#837069' }}>Geen diensten gevonden</h2>
            <p style={{ color: '#b6a092' }}>Probeer een andere zoekterm of filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesList;